import { Check, Clock, Copy, Gift } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDiscountRemaining } from './onboardingDiscount';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';

const COPIED_RESET_MS = 1800;

interface DiscountOfferModalProps {
  open: boolean;
  code: string;
  expiresAt: number;
  onClose: () => void;
  onRedeem: () => void;
  onUseLater: () => void;
}

export const DiscountOfferModal = ({
  open,
  code,
  expiresAt,
  onClose,
  onRedeem,
  onUseLater,
}: DiscountOfferModalProps) => {
  const { t } = useTranslation('getting_started');
  const [remaining, setRemaining] = useState(() => expiresAt - Date.now());
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open) return;
    setRemaining(expiresAt - Date.now());
    const id = setInterval(() => setRemaining(expiresAt - Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, expiresAt]);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const copySilently = () => {
    void navigator.clipboard.writeText(code);
  };

  const handleCopyClick = () => {
    if (isCopied) return;
    copySilently();
    setIsCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
  };

  const handleRedeem = () => {
    onRedeem();
  };

  const handleUseLater = () => {
    copySilently();
    toast.success(t('discount_modal.code_copied_toast'));
    onUseLater();
  };

  const handleClose = () => {
    onClose();
  };

  const isExpired = remaining <= 0;
  const isUrgent = !isExpired && remaining < 30 * 60 * 1000;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-md" data-testid="gs-discount-modal">
        <div className="flex flex-col gap-5">
          <span
            className={`inline-flex w-fit items-center gap-1.5 rounded-full bg-destructive-muted px-2 py-0.5 text-xs font-semibold text-destructive ${
              isUrgent ? 'animate-pulse' : ''
            }`}
          >
            <Clock className="size-3" />
            <span className="uppercase tracking-wide">{t('discount_modal.badge')}</span>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums" data-testid="gs-discount-modal-timer">
              {isExpired ? t('discount_modal.expired') : formatDiscountRemaining(remaining)}
            </span>
          </span>

          <div className="flex flex-col gap-3">
            <DialogTitle className="text-xl">
              <span className="inline-flex size-8 items-center justify-center rounded-lg bg-lime-400">
                <Gift className="size-5 text-white" />
              </span>
              {t('discount_modal.title')}
            </DialogTitle>
            <DialogDescription>{t('discount_modal.description')}</DialogDescription>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCopyClick}
          disabled={isCopied}
          className="group flex items-center justify-between gap-3 rounded-lg border border-dashed border-lime-400 bg-lime-100 px-3 py-3 text-left transition-colors hover:bg-lime-200 cursor-pointer disabled:cursor-default disabled:hover:bg-lime-100"
          data-testid="gs-discount-modal-copy"
        >
          <span className="font-mono text-sm font-medium tracking-wider text-lime-900">{code}</span>
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              isCopied ? 'text-lime-800' : 'text-lime-800 group-hover:text-lime-900'
            }`}
          >
            {isCopied ? (
              <>
                <Check className="size-3.5" />
                {t('discount_modal.code_copied')}
              </>
            ) : (
              <>
                <Copy className="size-3.5" />
                {t('discount_modal.copy_code')}
              </>
            )}
          </span>
        </button>

        <div className="flex flex-col gap-2">
          <Button
            variant="dark"
            size="md"
            onClick={handleRedeem}
            disabled={isExpired}
            data-testid="gs-discount-modal-redeem"
          >
            {t('discount_modal.redeem_now')}
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={handleUseLater}
            data-testid="gs-discount-modal-use-later"
          >
            {t('discount_modal.use_later')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
